<script setup lang="ts">
    import { onMounted } from 'vue';
    import { Field } from 'vee-validate';
    import { useStemOverlayViewModel } from '@/views/3d-planner/stem/useStemOverlayViewModel';
    import type { SelectableStemSize } from '@/views/3d-planner/stem/stem.types';
    import { PLANNER_TARGETS } from '@/domain/analytics/analyticsTargets';

    const vm = useStemOverlayViewModel();

    // Re-sync the toggle buttons to the current selection each time the panel opens.
    onMounted(vm.refreshToggleButtons);
</script>

<template>
    <!-- Stem Selection Header -->
    <div class="stem-selection-header mb-2">
        <div class="text-subtitle-1">Stem Selection</div>
    </div>

    <!-- First Toggle: Preferred vs Alternative Stems -->
    <div class="app-planner-stem-input-group mb-4">
        <app-toggle
            v-model="vm.selectedStemSystem"
            :class="vm.hasAlternativeStems ? '' : 'd-none'"
            :options="[
                { label: 'Preferred Stems', value: 'preferred' },
                { label: 'Alternative Stems', value: 'alternative' },
            ]"
        />
    </div>

    <!-- Second Toggle: Formus Recommended vs Manual Selection -->
    <div class="app-planner-stem-input-group mb-4">
        <app-toggle
            v-model="vm.selectionMode"
            :options="[
                {
                    label: 'Formus Recommended',
                    value: 'recommended',
                    disabled: !vm.hasRecommendedStems,
                },
                { label: 'Manual Selection', value: 'manual' },
            ]"
        />
    </div>

    <!-- Formus Recommended: Card-based Selection -->
    <div v-if="vm.isRecommendedMode">
        <div v-if="!vm.hasRecommendedStems" class="pa-1">
            <div class="d-flex align-center pa-3 rounded">
                <v-icon class="mr-2 text-info" size="20" color="grey-text">
                    mdi-information-outline
                </v-icon>
                <span class="text-body-2 text-black"> There is no recommended Stem available </span>
            </div>
        </div>

        <div v-else class="stem-cards-container" :data-analytics-id="PLANNER_TARGETS.stemContainer">
            <v-card
                v-for="card in vm.recommendedStemCards"
                :key="card.stem.stemUrl"
                :class="[
                    'stem-card',
                    'mb-3',
                    card.selected ? 'stem-card-selected' : 'stem-card-unselected',
                ]"
                elevation="0"
                @click="vm.clickRecommendedStem(card.stem)"
            >
                <v-card-text class="d-flex align-center justify-space-between pa-4">
                    <div class="flex-grow-1">
                        <div class="stem-card-title">
                            <strong>{{ card.sizeLabel }}</strong>
                            {{ card.descriptor }}
                        </div>
                    </div>
                    <div class="stem-card-radio">
                        <div :class="['radio-indicator', card.selected ? 'radio-selected' : '']">
                            <div v-if="card.selected" class="radio-dot"></div>
                        </div>
                    </div>
                </v-card-text>
            </v-card>
        </div>
    </div>

    <!-- Manual Selection: Dropdown Selectors -->
    <div v-if="vm.isManualMode" class="manual-selection-container">
        <!-- System + Type Dropdown -->
        <div class="mb-3">
            <div class="text-body-2 text-black mb-2">System</div>
            <Field
                v-slot="{ field, errors }"
                v-model="vm.selectedSystemProfile"
                name="stem-selected-system-profile"
                :rules="{ required: true }"
                validate-on-blur
                validate-on-mount
            >
                <v-select
                    v-bind="field"
                    v-model="vm.selectedSystemProfile"
                    :items="vm.selectableSystemProfiles"
                    item-title="name"
                    return-object
                    variant="outlined"
                    density="comfortable"
                    :error-messages="
                        errors.length ? 'Please select a system profile for the new stem' : ''
                    "
                    hide-details="auto"
                    bg-color="white"
                />
            </Field>
        </div>

        <!-- Offset Dropdown -->
        <div class="mb-3">
            <div class="text-body-2 text-black mb-2">Offset</div>
            <Field
                v-slot="{ field, errors }"
                v-model="vm.selectedOffset"
                name="stem-selected-offset"
                :rules="{ required: true }"
                validate-on-blur
                validate-on-mount
            >
                <v-select
                    v-bind="field"
                    v-model="vm.selectedOffset"
                    :items="vm.selectableStemOffsets"
                    item-title="offset"
                    item-value="offset"
                    variant="outlined"
                    density="comfortable"
                    :error-messages="
                        errors.length ? 'Please select an offset for the new stem' : ''
                    "
                    hide-details="auto"
                    bg-color="white"
                />
            </Field>
        </div>

        <!-- Size Dropdown -->
        <div class="mb-3">
            <div class="text-body-2 text-black mb-2">Size</div>
            <Field
                v-slot="{ field, errors }"
                v-model="vm.selectedSize"
                name="stem-selected-size"
                :rules="{ required: true }"
                validate-on-blur
                validate-on-mount
            >
                <v-select
                    v-bind="field"
                    v-model="vm.selectedSize"
                    :items="vm.sizeItems"
                    item-title="size"
                    item-value="size"
                    variant="outlined"
                    density="comfortable"
                    :error-messages="errors.length ? 'Please select a size for the new stem' : ''"
                    hide-details="auto"
                    bg-color="white"
                >
                    <template #item="{ props, item }">
                        <v-list-subheader v-if="item.value?.header">
                            {{ item.value.header }}
                        </v-list-subheader>
                        <v-divider v-else-if="item.value?.divider" />
                        <v-list-item v-else v-bind="props">
                            <template #prepend>
                                <v-icon
                                    v-if="(item.raw as SelectableStemSize)?.suitable"
                                    color="green"
                                >
                                    mdi-check-bold
                                </v-icon>
                                <v-icon v-else color="red">mdi-alert-outline</v-icon>
                            </template>
                            {{ props.size }}
                        </v-list-item>
                    </template>
                </v-select>
            </Field>
        </div>

        <!-- Head Offset Dropdown -->
        <div class="mb-3">
            <div class="text-body-2 text-black mb-2">Head Offset</div>
            <Field
                v-slot="{ field, errors }"
                v-model="vm.selectedHeadOffset"
                name="stem-selected-head-offset"
                :rules="{ required: true }"
                validate-on-blur
                validate-on-mount
            >
                <v-select
                    v-bind="field"
                    v-model="vm.selectedHeadOffset"
                    :items="vm.selectableHeadOffsets"
                    item-title="text"
                    return-object
                    variant="outlined"
                    density="comfortable"
                    :error-messages="
                        errors.length ? 'Please select a head offset for the new stem' : ''
                    "
                    hide-details="auto"
                    bg-color="white"
                />
            </Field>
        </div>
    </div>

    <!-- Note about changing stem selection -->
    <div v-if="vm.showSelectionNote" class="d-flex align-center bg-surface-light rounded mt-4">
        <v-icon class="mr-2 text-info" size="20" color="grey-text">mdi-information-outline</v-icon>
        <div class="text-caption text-black">
            Note: Changing Stem selection will reset manual stem placement to Formus recommended
            positioning
        </div>
    </div>
</template>

<style scoped lang="scss">
    .app-planner-stem-input-group {
        margin-bottom: 16px;
    }

    .stem-selection-header {
        color: var(--formus-palette-text-grey, #595959);
        font-size: 16px;
        font-weight: 400;
        line-height: 26px;
    }

    .stem-cards-container {
        display: flex;
        flex-direction: column;
        gap: 0;
    }

    .stem-card {
        border: 1px solid #dbe0e5;
        border-radius: 12px;
        cursor: pointer;
        transition: all 0.2s ease;
        background-color: white;
    }

    .stem-card-selected {
        background-color: var(--page-background, #f0f2f5) !important;
        border-color: #dbe0e5;
    }

    .stem-card-unselected {
        background-color: white;
        border-color: #dbe0e5;
    }

    .stem-card:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .stem-card-title {
        font-size: 14px;
        line-height: 21px;
        color: #141414;
    }

    .stem-card-title strong {
        font-weight: 700;
        font-size: 16px;
    }

    .radio-indicator {
        width: 20px;
        height: 20px;
        border: 2px solid #dbe0e5;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
    }

    .radio-indicator.radio-selected {
        border-color: rgb(var(--v-theme-primary));
        background-color: white;
    }

    .radio-dot {
        width: 10px;
        height: 10px;
        border-radius: 5px;
        background-color: rgb(var(--v-theme-primary));
    }

    .text-black {
        color: rgb(var(--v-theme-black));
    }

    .bg-surface-light {
        background-color: #f7f7f7;
    }

    .bg-surface-variant {
        background-color: var(--bg-mid-grey, #f0f2f5);
    }

    @media screen and (max-width: 820px) {
        .app-planner-stem-input-group * {
            font-size: 12px !important;
        }
    }
</style>
